import React, {useMemo} from 'react';
import {useGetMessages} from '../../../hooks';
import DoneIcon from '@material-ui/icons/Done';
import NotDoneIcon from '@material-ui/icons/Clear';
import InfoIcon from '@material-ui/icons/Info';
import {FormControlLabelProps} from '@material-ui/core/FormControlLabel';
import {PayoutSetupInfo, PayoutTypes} from '../../../../shared/types';
import {formatCurrency} from '../../../utils/Utils';
import {
    isPayoutThresholdEligible,
    isTaxInterviewComplete,
    isPayoutMethodComplete,
    isPaymentConsentComplete,
    isPayoutEligible
} from '../../../utils/ValidationUtils';

interface ListItem {
    text: string;
    icon?: JSX.Element;
    complete?: boolean;
    action?: React.ReactNode;
}

interface PayoutOption extends Omit<FormControlLabelProps, 'label' | 'value' | 'control'> {
    label: any;
    value: PayoutTypes;
    listItems: ListItem[];
}

const useGetPayoutOptions = (
    payoutSetupInfo: PayoutSetupInfo,
    amount: number = 0
): PayoutOption[] => {
    const messages = useGetMessages(
        {
            optionA: {
                code: 'epic.affiliate.closeAccount.selection.payout.v2',
                args: [<strong key="amount">{formatCurrency(amount)}</strong>]
            },
            reqBalance: {
                code: 'epic.affiliate.closeAccount.selection.payout.reqBalance.v2',
                args: [formatCurrency(100)]
            },
            reqTaxInterview: 'epic.affiliate.closeAccount.selection.payout.reqTaxInterview.v2',
            reqPayoutMethod: 'epic.affiliate.closeAccount.selection.payout.reqPayoutMethod.v2',
            reqConsent: 'epic.affiliate.closeAccount.selection.payout.reqPaymentConsent.v2',
            optionB: 'epic.affiliate.closeAccount.selection.noPayout.v2',
            optionB_caption: 'epic.affiliate.closeAccount.selection.noPayout.caption.v2',
            completeNow: 'epic.affiliate.common.completeNow'
        },
        [amount]
    );

    const mapListItem = (item: ListItem) => {
        if (!item.complete) {
            item.icon = <NotDoneIcon color="error" />;
            item.action = messages.completeNow;
        } else {
            item.icon = <DoneIcon htmlColor="#11CF59" />;
        }
        return item;
    };

    const payoutListItems: ListItem[] = [
        {
            text: messages.reqBalance,
            complete: isPayoutThresholdEligible(amount)
        },
        {
            text: messages.reqTaxInterview,
            complete: isTaxInterviewComplete(payoutSetupInfo)
        },
        {
            text: messages.reqPayoutMethod,
            complete: isPayoutMethodComplete(payoutSetupInfo)
        },
        {
            text: messages.reqConsent,
            complete: isPaymentConsentComplete(payoutSetupInfo)
        }
    ];

    return useMemo(
        () => [
            {
                label: messages.optionA,
                value: PayoutTypes.CASH,
                disabled: !isPayoutEligible({payoutSetupInfo}, amount),
                listItems: payoutListItems.map(mapListItem)
            },
            {
                label: messages.optionB,
                value: PayoutTypes.NONE,
                listItems: [{icon: <InfoIcon color="secondary" />, text: messages.optionB_caption}]
            }
        ],
        [payoutSetupInfo, messages.optionA, messages.optionB]
    );
};

export default useGetPayoutOptions;
